"use client";

import * as React from "react";
import { useChromeStrings } from "./chrome-strings";
import {
  X,
  Link2,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  MessageCircle,
  Send,
  Copy,
  Check,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { PopoverContent } from "@/components/ui/popover";
import {
  articlePopoverCompactSheetClass,
  useHeaderPopoverLayout,
  type ArticlePopoverPlacement,
} from "./popover-placement";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";

type Channel = {
  key: string;
  label: string | null;
  Icon: LucideIcon;
};

const CHANNELS: Channel[] = [
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "x", label: "X", Icon: Twitter },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
  { key: "telegram", label: "Telegram", Icon: Send },
  // Brand names are not translated; only this one is a word.
  { key: "email", label: null, Icon: Mail },
];

export function SharePopoverContent({
  onClose,
  placement = "rail",
}: {
  onClose: () => void;
  placement?: ArticlePopoverPlacement;
}) {
  const t = useChromeStrings("share");
  const layout = useHeaderPopoverLayout(placement, {
    desktopWidth: 320,
    headerAlign: "start",
  });
  const padX = layout.isCompactHeader ? 16 : 24;

  return (
    <PopoverContent
      side={layout.side}
      align={layout.align}
      sideOffset={12}
      collisionPadding={layout.collisionPadding}
      className={`bg-white p-0${layout.isCompactHeader ? ` ${articlePopoverCompactSheetClass()}` : ""}`}
      style={{
        zIndex: 60,
        width: layout.width,
        borderRadius: "8px",
        border: `1px solid rgba(0,0,0,0.15)`,
        fontFamily: CORE_FONT.sans,
      }}
    >
      <div className="flex w-full min-w-0 flex-col">
        <PopoverHeader title={t("title")} onClose={onClose} padX={padX} />
        <SocialIconsGrid padX={padX} />
        <CopyLinkRow padX={padX} />
      </div>
    </PopoverContent>
  );
}

export function PopoverHeader({
  title,
  onClose,
  padX = 24,
}: {
  title: string;
  onClose: () => void;
  padX?: number;
}) {
  const t = useChromeStrings("share");
  return (
    <div
      className="relative flex items-center justify-center pt-[20px] pb-[16px]"
      style={{ paddingLeft: padX, paddingRight: padX }}
    >
      <h2
        style={{
          fontFamily: CORE_FONT.sans,
          fontSize: "15px",
          fontWeight: 700,
          color: CORE_COLORS.textPrimary,
          lineHeight: "20px",
        }}
      >
        {title}
      </h2>
      <button
        type="button"
        onClick={onClose}
        aria-label={t("close")}
        className="absolute right-[12px] top-[12px] inline-flex items-center justify-center size-[36px] rounded-full transition-colors duration-150 hover:bg-[#f5f5f7] active:scale-[0.95] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:var(--focus-ring)]"
        style={
          {
            ["--focus-ring" as string]: CORE_COLORS.brandGreen,
          } as React.CSSProperties
        }
      >
        <X
          className="size-[18px]"
          color={CORE_COLORS.textPrimary}
          strokeWidth={2}
        />
      </button>
    </div>
  );
}

export function SocialIconsGrid({ padX = 24 }: { padX?: number }) {
  const t = useChromeStrings("share");
  return (
    <div
      className="grid grid-cols-3 gap-y-[20px] gap-x-[12px] pt-[8px] pb-[16px]"
      style={{ paddingLeft: padX, paddingRight: padX }}
    >
      {CHANNELS.map(({ key, label: brand, Icon }) => {
        // Brand names stay as they are; `null` marks the one that is a word.
        const label = brand ?? t("email");
        return (
        <button
          key={key}
          type="button"
          aria-label={label}
          className="group flex flex-col items-center gap-[8px] min-h-[72px] focus-visible:outline-none"
        >
          <span
            className="inline-flex items-center justify-center size-[48px] rounded-full transition-[background-color,transform] duration-150 ease-out motion-reduce:transition-none group-hover:bg-[color:var(--green-dark)] group-active:scale-[0.94] group-focus-visible:ring-2 group-focus-visible:ring-offset-2 group-focus-visible:ring-[color:var(--green)]"
            style={
              {
                backgroundColor: CORE_COLORS.brandGreen,
                ["--green" as string]: CORE_COLORS.brandGreen,
                ["--green-dark" as string]: CORE_COLORS.brandGreenDeep,
              } as React.CSSProperties
            }
          >
            <Icon className="size-[20px]" color="white" strokeWidth={1.8} />
          </span>
          <span
            className="text-center group-hover:text-[color:var(--green-dark)] transition-colors duration-150 motion-reduce:transition-none"
            style={
              {
                fontFamily: CORE_FONT.sans,
                fontSize: "11px",
                color: CORE_COLORS.textPrimary,
                lineHeight: "14px",
                ["--green-dark" as string]: CORE_COLORS.brandGreenDeep,
              } as React.CSSProperties
            }
          >
            {label}
          </span>
        </button>
        );
      })}
    </div>
  );
}

export function CopyLinkRow({
  url,
  padX = 24,
}: { url?: string; padX?: number } = {}) {
  const t = useChromeStrings("share");
  const [copied, setCopied] = React.useState(false);

  const fullUrl =
    url ??
    (typeof window !== "undefined"
      ? window.location.href
      : "https://www.hkej.com/article-detail");

  const handleCopy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(fullUrl).catch(() => {});
    }
    setCopied(true);
    toast(t("copied"), { duration: 2000 });
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div
      className="flex flex-col gap-[8px] py-[16px]"
      style={{
        borderTop: `1px solid ${CORE_COLORS.dividerSoft}`,
        paddingLeft: padX,
        paddingRight: padX,
      }}
    >
      <span
        style={{
          fontFamily: CORE_FONT.sans,
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
          color: CORE_COLORS.textTertiary,
          lineHeight: "14px",
        }}
      >
        {t("linkHeading")}
      </span>
      <div className="flex min-w-0 items-stretch gap-[8px]">
        <input
          type="text"
          value={fullUrl}
          readOnly
          aria-label={t("link")}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 truncate"
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: "13px",
            color: CORE_COLORS.textPrimary,
            lineHeight: "20px",
            padding: "8px 12px",
            border: `1px solid rgba(0,0,0,0.15)`,
            borderRadius: "3px",
            backgroundColor: "#f9f9fa",
          }}
        />
        <button
          type="button"
          aria-label={t("copy")}
          onClick={handleCopy}
          className="inline-flex items-center gap-[6px] shrink-0 transition-colors hover:bg-[color:var(--green-dark)]"
          style={
            {
              fontFamily: CORE_FONT.sans,
              fontSize: "13px",
              fontWeight: 700,
              color: "white",
              lineHeight: "20px",
              padding: "8px 14px",
              backgroundColor: CORE_COLORS.brandGreen,
              borderRadius: "3px",
              ["--green-dark" as string]: CORE_COLORS.brandGreenDark,
            } as React.CSSProperties
          }
        >
          {copied ? (
            <>
              <Check className="size-[14px]" strokeWidth={2.4} />
              {t("copiedShort")}
            </>
          ) : (
            <>
              <Copy className="size-[14px]" strokeWidth={2.2} />
              {t("copyShort")}
            </>
          )}
        </button>
      </div>
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? t("copiedToast") : ""}
      </span>
    </div>
  );
}
